import { DataStore } from "aws-amplify/datastore";
import { format } from "date-fns";
import { Client, Service as AmpService, ServiceRendered } from "../../models";
import { ActivityRepository } from "../interfaces";
import { ServiceRecord, ServiceRenderedRecord } from "../types";
import { roundToPence } from "../../utils/money";

export const toServiceRenderedRecord = async (sr: ServiceRendered): Promise<ServiceRenderedRecord> => {
  const client = await sr.client;
  const service = await sr.service;
  return {
    id: sr.id,
    date: sr.date,
    invoiceID: sr.invoiceID,
    quantity: sr.quantity,
    mileage: sr.mileage,
    mileageCost: sr.mileageCost,
    serviceCost: sr.serviceCost,
    clientId: client.id,
    serviceId: service.id,
    serviceName: service.name,
  };
};

export class DatastoreActivityRepository implements ActivityRepository {
  async listServices(): Promise<ServiceRecord[]> {
    return await DataStore.query(AmpService);
  }

  async saveService(service: Partial<ServiceRecord>): Promise<ServiceRecord> {
    return await DataStore.save(new AmpService({ name: service.name ?? "", cost: service.cost ?? 0, ...service }));
  }

  async updateService(service: ServiceRecord): Promise<void> {
    const original = await DataStore.query(AmpService, service.id);
    if (!original) throw new Error("Failed to find service to update");
    await DataStore.save(AmpService.copyOf(original, (draft) => { Object.assign(draft, service); }));
  }

  async deleteService(id: string): Promise<void> {
    await DataStore.delete(AmpService, id);
  }

  async getRenderedForInvoice(invoiceId: string): Promise<ServiceRenderedRecord[]> {
    const srs = await DataStore.query(ServiceRendered, (s) => s.invoiceID.eq(invoiceId));
    return Promise.all(srs.map(toServiceRenderedRecord));
  }

  async saveRendered(
    date: Date,
    clientId: string,
    serviceId: string,
    mileage: string,
    quantity: string,
    mileageCost: number
  ): Promise<void> {
    const service = await DataStore.query(AmpService, serviceId);
    const client = await DataStore.query(Client, clientId);
    if (!client || !service) throw new Error("Unable to find client or service for this activity");

    await DataStore.save(
      new ServiceRendered({
        date: format(date, "yyyy-MM-dd"),
        client,
        service,
        mileage: parseFloat(mileage),
        quantity: Number(quantity),
        mileageCost: roundToPence(mileageCost),
        serviceCost: service.cost,
      })
    );
  }

  async updateRendered(
    id: string,
    date: string,
    clientId: string,
    serviceId: string,
    mileage: string,
    quantity: string,
    mileageCost: number,
    serviceCost: number
  ): Promise<void> {
    const original = await DataStore.query(ServiceRendered, id);
    if (!original) throw new Error("Failed to find service rendered to update");
    await DataStore.save(
      ServiceRendered.copyOf(original, (draft) => {
        Object.assign(draft, {
          id,
          date,
          service: { id: serviceId },
          mileage: parseFloat(mileage),
          quantity: Number(quantity),
          mileageCost: roundToPence(mileageCost),
          serviceCost,
        });
      })
    );
  }

  async deleteRendered(id: string): Promise<void> {
    await DataStore.delete(ServiceRendered, id);
  }
}
